import { type Application, isWorkmanCardPassing } from "../types";

/** Plain-language suggestion for what the officer should do next */
export function getFriendlyNextStep(app: Application): {
  title: string;
  body: string;
  to: string;
  cta: string;
} {
  const base = `/applications/${app.id}`;
  const v = app.validations;

  if (app.holdReasons.length > 0) {
    return {
      title: "This file is on hold",
      body: "Review the reasons on this page, fix the issues in the field or with documents, then update the checklist on the Validation tab.",
      to: `${base}/validation`,
      cta: "Go to Validation",
    };
  }

  if (app.status === "icici_recorded") {
    return {
      title: "Next: link the subcontractor",
      body: "Banking and MOU are saved. Open the Subcontractor tab, pick the firm from the onboarded list, then save. You may attach this beneficiary’s work order if you have it. Register new firms under Subcontractor Onboarding in the main menu if needed.",
      to: `${base}/subcontractor`,
      cta: "Go to Subcontractor",
    };
  }

  if (app.status === "subcontractor_linked") {
    return {
      title: "Subcontractor on record",
      body: "The firm is linked for this beneficiary. Milestones and Bills stay coming soon for now; open other sections from the menu to review or update details.",
      to: `${base}/beneficiary`,
      cta: "Review beneficiary details",
    };
  }

  if (app.status === "ready_for_icici") {
    return {
      title: "Next: enter ICICI banking details",
      body: "Checks and Aadhaar seeding are done. Open the ICICI & MOU tab and enter the ICICI account number, IFSC, and branch, then save. You can attach the MOU scan in the same visit or after.",
      to: `${base}/banking`,
      cta: "Go to ICICI & MOU",
    };
  }

  if (app.status === "ready_for_mou") {
    return {
      title: "Next: attach the signed MOU",
      body: "ICICI account details are saved. On the ICICI & MOU tab, upload the signed MOU scan and press save to move this file to awaiting subcontractor.",
      to: `${base}/banking`,
      cta: "Go to ICICI & MOU",
    };
  }

  if (!v.aadhaarSeedingConfirmed) {
    const checksDone =
      v.nameMatchAcrossSources === true &&
      v.aadhaarVerified === true &&
      v.bankAccountVerified === true &&
      isWorkmanCardPassing(v.workmanCardAcceptable);
    if (!checksDone) {
      return {
        title: "Next: finish the verification checklist",
        body: "Use the dropdowns to record each check. When everything is marked OK, you can confirm Aadhaar seeding on the same tab.",
        to: `${base}/validation`,
        cta: "Go to Validation",
      };
    }
    return {
      title: "Next: confirm Aadhaar seeding",
      body: "All checklist items are OK. Press “Confirm Aadhaar seeding” and complete the popup. After that, the ICICI tab will unlock.",
      to: `${base}/validation`,
      cta: "Go to Validation",
    };
  }

  if (app.status === "pending_validation") {
    return {
      title: "Next: complete verification",
      body: "Work through the Validation tab: name match, Aadhaar, bank account, workman card, then seeding confirmation.",
      to: `${base}/validation`,
      cta: "Go to Validation",
    };
  }

  return {
    title: "Continue from Overview",
    body: "Use the coloured steps above to see where this file is. Pick a section on the left when you are ready to enter or review data.",
    to: `${base}/beneficiary`,
    cta: "View beneficiary & documents",
  };
}
